import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Shared Excel/PDF export helpers for the centralized Reports module.
 * Mirrors the existing `exportToCSV` contract in ../exportUtils.js:
 *   columns: [{ header: 'Name', key: 'name' }, ...]
 *   rows:    array of plain objects (row[key] read directly — format
 *            dates/currency into display strings before calling these)
 * so every report can pass the exact same columns/rows to CSV, Excel and
 * PDF export without three separate column mappings.
 */

// Report columns render currency as a display string ("₹1,234") shared
// identically across CSV/Excel/PDF/screen — correct for CSV/PDF/screen, but
// an Excel cell holding "₹1,234" as text can't be summed/sorted numerically
// in the workbook. Only the Excel export path unpacks it back to a plain
// number (still 1234, still correct); CSV/PDF/on-screen rendering are
// untouched.
const CURRENCY_STRING_RE = /^₹-?[\d,]+(\.\d+)?$/;

const unwrapCurrencyForExcel = (value) => {
  if (typeof value === 'string' && CURRENCY_STRING_RE.test(value)) {
    const numeric = Number(value.replace(/[₹,]/g, ''));
    if (Number.isFinite(numeric)) return numeric;
  }
  return value;
};

// xlsx was already an installed-but-unused dependency (see Reports module
// audit) — this is its first real usage in the app.
export const exportReportToExcel = (columns, rows, fileName, sheetName = 'Report') => {
  const data = rows.map((row) =>
    columns.reduce((acc, c) => {
      acc[c.header] = unwrapCurrencyForExcel(row[c.key] ?? '');
      return acc;
    }, {})
  );

  const worksheet = XLSX.utils.json_to_sheet(data, { header: columns.map((c) => c.header) });

  // Reasonable column widths from the longer of header/content, capped so a
  // long remarks/title column doesn't blow out the sheet.
  worksheet['!cols'] = columns.map((c) => {
    const maxLen = rows.reduce((max, row) => Math.max(max, String(row[c.key] ?? '').length), c.header.length);
    return { wch: Math.min(Math.max(maxLen + 2, 10), 45) };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

/**
 * options: { title, subtitle, filtersText, orientation: 'portrait'|'landscape', totalsText }
 */
export const exportReportToPDF = (columns, rows, fileName, options = {}) => {
  const { title = 'Report', subtitle = '', filtersText = '', orientation = 'landscape', totalsText = '' } = options;

  // Wide reports (8+ columns) default to landscape so columns don't clip —
  // callers can still force portrait for narrow reports.
  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Community Library Management System', pageWidth / 2, 32, { align: 'center' });

  doc.setFontSize(12);
  doc.text(title, pageWidth / 2, 50, { align: 'center' });

  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  let y = 66;
  if (subtitle) { doc.text(subtitle, pageWidth / 2, y, { align: 'center' }); y += 14; }
  if (filtersText) { doc.text(filtersText, pageWidth / 2, y, { align: 'center' }); y += 14; }

  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 40, y + 4);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: y + 14,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => String(row[c.key] ?? ''))),
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 30, right: 30 },
    didDrawPage: () => {
      const pageCount = doc.internal.getNumberOfPages();
      const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(110);
      doc.text(
        `Page ${pageCurrent} of ${pageCount}`,
        pageWidth - 60,
        doc.internal.pageSize.getHeight() - 20
      );
    }
  });

  if (totalsText) {
    const finalY = doc.lastAutoTable.finalY || y + 14;
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text(totalsText, 40, finalY + 20);
  }

  doc.save(`${fileName}.pdf`);
};

// `01-Aug-2026` style, matching the exact example filenames in the request.
export const formatFileDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${day}-${month}-${d.getFullYear()}`;
};

export const buildReportFileName = (baseName, fromDate, toDate) => {
  if (fromDate && toDate) return `${baseName}_${formatFileDate(fromDate)}_to_${formatFileDate(toDate)}`;
  if (fromDate) return `${baseName}_${formatFileDate(fromDate)}`;
  return `${baseName}_${formatFileDate(new Date())}`;
};
